import '../../App.css'
import {
    Badge,
    Box,
    Button,
    Code,
    HStack,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spacer,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Thead,
    Tooltip,
    Tr
} from '@chakra-ui/react'
import {
    AddSessionModalContent,
    SenderTargetFormWrapper,
    SenderTargetRow,
} from './styled'
import { useCallback, useState } from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { MdOutlineArrowForward, MdOutlineRadioButtonChecked, MdOutlineArrowBack } from 'react-icons/md'
import { sessionManageAction } from './sessionManageAction'
import { AddSessionForm } from '../AddSessionForm/AddSessionForm'
import { SeqNumModal } from '../SeqNumModal/SeqNumModal'
import { ConfirmActionModal } from '../ConfirmActionModal/ConfirmActionModal'
import { AddIcon, CheckIcon } from '@chakra-ui/icons'
import { isDownstreamSession, isRouterSession } from '../utils'
import { CustomTooltip } from '../common/CustomTooltip'
import { FiAlertTriangle, FiEdit3, FiEye, FiPause, FiPlay, FiSearch, FiTrash2 } from 'react-icons/fi'
import { CustomTh, CustomTr } from '../../../fix-search/components/ParsedMessageTable/styled'
import { TruncatedTd } from '../common/TruncatedTableCell'
import { startDemo } from '../../../fix-search/API'

const InvalidStatus = ({ isSelected }) => (
    <Tooltip
        label='Invalid session configuration. Session is not added in FIX Engine'
    >
        <Badge variant='solid' backgroundColor={isSelected ? '#b00' : 'service-red'}>invalid</Badge>
    </Tooltip>
)

const InactiveStatus = ({ isSelected }) => (
    <Tooltip
        label='Session is not active (can be activated manually or by schedule)'
    >
        <Badge variant='solid' backgroundColor={isSelected ? 'gray.800' : 'gray.600'}>inactive</Badge>
    </Tooltip>
)

const OfflineStatus = ({ isSelected }) => (
    <Tooltip
        label='Session is active, but not connected to counter party'
    >
        <Badge variant='solid' backgroundColor={isSelected ? 'orange.500' : 'yellow.500'} >offline</Badge>
    </Tooltip>
)

const OnlineStatus = ({ isSelected }) => (
    <Tooltip
        label='Session is active and connected to counter party'
    >
        <Badge variant='solid' backgroundColor={isSelected ? 'green.700' : 'green.600'}  >online</Badge>
    </Tooltip>
)

const Status = ({isActive, isConnected, isSelected}) => (
    isActive === undefined ? <InvalidStatus isSelected={isSelected}/>
               : !isActive ? <InactiveStatus isSelected={isSelected}/>
            : !isConnected ? <OfflineStatus isSelected={isSelected}/>
                           : <OnlineStatus isSelected={isSelected}/>
)

export const SessionManager = ({ 
    config,
    sessions,
    routingRules,
    handleShowSearch,
    showError,
    errors,
    notify,
    demoStatus
}) => {
    const [isAdvanced, setIsAdvanced] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(null)
    const selectedSession = sessions[selectedIndex]

    const [sessionDefaults, setSessionDefaults] = useState({})

    const onRowSelect = (index) => {
        setSelectedIndex(index)
    }

    const { onOpen, onClose, isOpen } = useDisclosure()
    const { onOpen: onSeqNumOpen, onClose: onSeqNumClose, isOpen: isSeqNumOpen } = useDisclosure()
    const { onOpen: onDeleteModalOpen, onClose: onDeleteModalClose, isOpen: isDeleteModalOpen } = useDisclosure()

    const [routerWarnings, setRouterWarnings] = useState([])

    const onSeqNumSend = (type, value) => {
        if (type === 'read') {
            sessionManageAction('seqRead', selectedSession.sessionID, value)
        } else if (type === 'write') {
            sessionManageAction('seqWrite', selectedSession.sessionID, value)
        }
    }

    const onSearchClick = (sessionID) => {
        handleShowSearch({ sessions: [sessionID], query: '' })
    }

    const isSessionUsedInRoutingRules = useCallback((session) => {
        return isDownstreamSession(session.params)
            && routingRules.some(rule => rule.routes.includes(session.sessionID))
    }, [routingRules])

    const anySessionErrors = errors.some((error) => error.sessionID)

    const validateRouterSetup = (upstreamSession) => {
        const warnings = []

        const downstreamSessions = sessions.filter(session => isDownstreamSession(session.params))

        const inactiveRouterDownstreamSessions =
            downstreamSessions
                .filter(isSessionUsedInRoutingRules)
                .filter(downstreamSession => !downstreamSession.isActive)

        if (inactiveRouterDownstreamSessions.length) {
            warnings.push(<>
                <Text>Some Router Downstream sessions (used in routing rules) are <InactiveStatus /> or <InvalidStatus />:</Text>
                <TableContainer
                    border='1px solid'
                    borderColor='table-container-border'
                    borderRadius='5px'
                    paddingX={2}
                    marginTop={2}
                    width='fit-content'
                >  
                    <Table
                        style={{ font: 'var(--code-font)' }}
                        size='sm'
                    >
                        <Tbody>
                            {inactiveRouterDownstreamSessions.map(
                                (session, index) => {
                                    const isLastRow = index === (inactiveRouterDownstreamSessions.length - 1)
                                    return <Tr>
                                        <Td
                                            paddingX={1}
                                            borderBottom={isLastRow ? 'none' : undefined}
                                        >
                                            {session.sessionID}
                                        </Td>
                                        <Td
                                            paddingX={1}
                                            borderBottom={isLastRow ? 'none' : undefined}
                                            textAlign='center !important'
                                        >
                                            <Status isActive={session.isActive}/>
                                        </Td>
                                    </Tr>
                                }
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </>)
        }

        const downstreamSessionsWithNonMatchingFixVersions =
            downstreamSessions
                .filter(isSessionUsedInRoutingRules)
                .filter(downstreamSession => downstreamSession.params.fixVersion !== upstreamSession.params.fixVersion)
        if (downstreamSessionsWithNonMatchingFixVersions.length) {
            warnings.push(<>
                <Text>Some Router Downstream sessions (used in routing rules) have FIX versions which mismatch with Router Upstream session FIX version (<Code>{upstreamSession.params.fixVersion}</Code>):</Text>
                <TableContainer
                    border='1px solid'
                    borderColor='table-container-border'
                    borderRadius='5px'
                    paddingX={2}
                    marginTop={2}
                    width='fit-content'
                >  
                    <Table
                        style={{ font: 'var(--code-font)' }}
                        size='sm'
                    >
                        <Tbody>
                            {downstreamSessionsWithNonMatchingFixVersions.map(
                                (session, index) => {
                                    const isLastRow = index === (downstreamSessionsWithNonMatchingFixVersions.length - 1)
                                    return <Tr>
                                        <Td
                                            paddingX={1}
                                            borderBottom={isLastRow ? 'none' : undefined}
                                        >
                                            {session.sessionID}
                                        </Td>
                                        <Td
                                            paddingX={1}
                                            borderBottom={isLastRow ? 'none' : undefined}
                                        >
                                            <Code paddingY='2px'>{session.params.fixVersion}</Code>
                                        </Td>
                                    </Tr>
                                }
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </>)
        }

        if (warnings.length) {
            setRouterWarnings(warnings)
        } else {
            sessionManageAction('activate' , selectedSession.sessionID)
        }
    }

    return (<>
        <Modal isOpen={routerWarnings.length} size='2xl'>
            <ModalOverlay />
            <ModalContent backgroundColor='modal'>
                <ModalHeader><HStack><FiAlertTriangle color='red'/><Text>Router setup might be incorrect!</Text></HStack></ModalHeader>
                <ModalCloseButton onClick={() => setRouterWarnings([])} />
                <ModalBody>
                    <Text marginBottom={1}>
                        Are you sure you want to start Router Upstream session?
                    </Text>
                    {routerWarnings.map((warning, index) =>
                        <Box
                            key={index}
                            paddingTop={2}
                            marginTop={3}
                            borderTop='1px solid'
                            borderTopColor='table-container-border'
                        >
                            {warning}
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant='outline' mr={3} onClick={() => setRouterWarnings([])}>Discard</Button>
                    <Button onClick={() => {
                        sessionManageAction('activate' , selectedSession.sessionID)
                        setRouterWarnings([])
                    }}>
                    Activate
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        <SenderTargetFormWrapper>
            <HStack
                spacing='8px'
                marginBottom={4}
            >
                <Button
                    aria-label="Create new session"
                    leftIcon={<AddIcon />}
                    onClick={() => {
                        setSelectedIndex(null)
                        setSessionDefaults(undefined)
                        onOpen()
                    }}
                >
                    Add New Session
                </Button>
                {demoStatus !== 'NON_AVAILABLE' &&
                    <Button
                        aria-label="Start Demo"
                        variant='outline'
                        isLoading={demoStatus === 'IN_PROGRESS'}
                        loadingText={'Adding Demo Sessions'}
                        isDisabled={demoStatus === 'FINISHED' || demoStatus === 'FAILED'}
                        onClick={() => {
                            startDemo()
                        }}
                        spinnerPlacement='end'
                        rightIcon={
                            demoStatus === 'AVAILABLE' ? undefined
                            : demoStatus === 'FINISHED' ? <CheckIcon color='green.500'/>
                            : <FiAlertTriangle color={'var(--chakra-colors-service-red)'}/>
                        }
                    >
                        {
                            demoStatus === 'FINISHED' ? 'Demo Sessions Added!'
                            : demoStatus === 'FAILED' ? 'Failed to Add Demo Sessions!'
                            : 'Add Demo Sessions'
                        }
                    </Button>
                }
                <Spacer/>
                <CustomTooltip
                    label={({isNoSessionSelected, isSessionActive, isSessionInvalid, isRouterSession, isRoutingRulesDefined}) => 
                        isNoSessionSelected ? "" :
                        isSessionInvalid ? "Unable to activate invalid session." :
                        !isSessionActive && isRouterSession && !isRoutingRulesDefined ? "Unable to activate router session without routing rules" :
                        isSessionActive ? "Deactivate the selected session"
                            : 'Activate the selected session'
                    }
                    labelOnDisabled={'Select a session'}
                    childrenProps={{
                        isNoSessionSelected: !selectedSession,
                        isSessionActive: selectedSession && (selectedSession.isActive),
                        isSessionInvalid: selectedSession && (selectedSession.isActive === undefined),
                        isRouterSession: selectedSession && isRouterSession(selectedSession.params, config),
                        isRoutingRulesDefined: !!routingRules?.length
                    }}
                >
                    {({isNoSessionSelected, isSessionActive, isSessionInvalid, isRouterSession, isRoutingRulesDefined}) =>
                        <IconButton
                            isDisabled={isNoSessionSelected || isSessionInvalid || (!isSessionActive && isRouterSession && !isRoutingRulesDefined)}
                            aria-label="Activate"
                            onClick={() => {
                                if (isRouterSession && !isSessionActive && !isDownstreamSession(selectedSession.params)) {
                                    validateRouterSetup(selectedSession)
                                } else {
                                    sessionManageAction(isSessionActive ? 'deactivate' : 'activate' , selectedSession.sessionID)
                                }
                            }}
                            fontSize="20px"
                            icon={isSessionActive ? <FiPause /> : <FiPlay />}
                        />
                    }
                </CustomTooltip>
                <CustomTooltip
                    label={({isNoSessionSelected, isSessionActive}) => 
                        isSessionActive ? "View selected session configuration. Deactivate the session to edit." :
                        isNoSessionSelected ? ""
                        : 'Edit the selected session'
                    }
                    labelOnDisabled={'Select a session'}
                    childrenProps={{
                        isNoSessionSelected: !selectedSession,
                        isSessionActive: selectedSession && selectedSession.isActive
                    }}
                >
                    {({isNoSessionSelected, isSessionActive}) =>
                        <IconButton
                            isDisabled={isNoSessionSelected}
                            aria-label="Edit"
                            fontSize="20px"
                            onClick={() => {
                                onOpen()
                            }}
                            icon={isSessionActive ? <FiEye /> : <FiEdit3 />}
                        />
                    }
                </CustomTooltip>
                <CustomTooltip
                    label={({isNoSessionSelected, isSessionUsedInRoutingRules}) => 
                        isSessionUsedInRoutingRules ? "Unable to remove downstream session, which is defined in routing rules" :
                        isNoSessionSelected ? "" :
                        'Remove the selected session'
                    }
                    labelOnDisabled={'Select a session'}
                    childrenProps={{
                        isNoSessionSelected: !selectedSession,
                        isSessionUsedInRoutingRules: selectedSession && isSessionUsedInRoutingRules(selectedSession)
                    }}
                >
                    {({isNoSessionSelected, isSessionUsedInRoutingRules}) =>
                        <IconButton
                            isDisabled={isSessionUsedInRoutingRules || isNoSessionSelected}
                            aria-label="Delete"
                            onClick={() => {
                                onDeleteModalOpen()
                            }}
                            fontSize="17px"
                            icon={<FiTrash2 />}
                        />
                    }
                </CustomTooltip>
                <CustomTooltip
                    childrenProps={{
                        isNoSessionSelected: !selectedSession,
                        isInvalid: selectedSession && selectedSession.isActive === undefined,
                        isConnected: selectedSession && selectedSession.isConnected
                    }}
                    label={({isNoSessionSelected, isInvalid, isConnected}) => 
                        isInvalid ? "Unable to set sequence numbers for invalid session" :
                        isConnected ? "Unable to set sequence numbers for connected session, deactivate it first" :
                        isNoSessionSelected ? "" :
                        'Reset the sequence numbering for the selected session'
                    }
                    labelOnDisabled={'Select a session'}
                    
                >
                    {({isNoSessionSelected, isInvalid, isConnected}) =>
                        <Button
                            isDisabled={isNoSessionSelected || isInvalid || isConnected}
                            onClick={onSeqNumOpen} 
                        >
                            Set Seq#
                        </Button>
                    }
                </CustomTooltip>
            </HStack>
            <ConfirmActionModal
                isOpen={isDeleteModalOpen}
                onClose={onDeleteModalClose}
                onConfirm={() => {
                    sessionManageAction('removeSession', selectedSession.sessionID)
                    setSelectedIndex(null)
                    onDeleteModalClose()
                }}
            />
            <TableContainer>
            <Table
                style={{ tableLayout: 'fixed' }}
                size="sm" colorScheme="white" variant='simple' fontFamily='var(--code-font-family)'>
                <Thead>
                    <CustomTr>
                        <CustomTh>Sender:Target</CustomTh>
                        {config.router &&
                            <CustomTh
                                textAlign='center !important'
                                width='70px'
                            >Type</CustomTh>
                        }
                        <CustomTh
                            textAlign='right !important'
                            width='90px'
                        >ReadSeq</CustomTh>
                        <CustomTh
                            textAlign='right !important'
                            width='100px'
                        >WriteSeq</CustomTh>
                        <CustomTh
                            textAlign='center !important'
                            width={anySessionErrors ? '90px' : '106px'}
                            paddingRight={anySessionErrors ? '0' : undefined}
                        >Status</CustomTh>
                        {anySessionErrors && <CustomTh
                            textAlign='center !important'
                            width='26px'
                        ></CustomTh>}
                        <CustomTh
                            // width='0'
                            width='46px'
                        ></CustomTh>
                    </CustomTr>
                </Thead>
                <Tbody>
                    {/* <SenderTargetRow isEven>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                    </SenderTargetRow>
                    <SenderTargetRow>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                    </SenderTargetRow>
                    <SenderTargetRow isEven>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                        <Td><Skeleton height='20px'/></Td>
                    </SenderTargetRow> */}
                    {sessions.map((session, index) => {
                        const { sessionID, params, rSeq, wSeq, isActive, isConnected } = session
                        const isSelected = sessionID === selectedSession?.sessionID
                        const sessionErrors = errors.filter((e) => e.sessionID === sessionID)
                        const isEven = index % 2 === 0
                        return (
                            <SenderTargetRow
                                onClick={() => onRowSelect(index)}
                                isSelected={isSelected}
                                isEven={isEven}
                                key={sessionID}
                            >
                                <TruncatedTd isTruncate isEven={isEven} isSelected={isSelected}>{sessionID}</TruncatedTd>
                                {config.router &&
                                    <Td>
                                        {!isRouterSession(params, config)
                                            ?   <Tooltip label='Non Routing'>
                                                    <span><MdOutlineRadioButtonChecked style={{ margin: 'auto' }}/></span>
                                                </Tooltip>
                                            : (isDownstreamSession(params) 
                                                ?   <Tooltip label='Downstream'>
                                                        <span><MdOutlineArrowForward style={{ margin: 'auto' }}/></span>
                                                    </Tooltip>
                                                :   <Tooltip label='Upstream'>
                                                        <span><MdOutlineArrowBack style={{ margin: 'auto' }}/></span>
                                                    </Tooltip>)
                                        }
                                    </Td>
                                }
                                <Td textAlign='right'>{rSeq === undefined ? '—' : rSeq}</Td>
                                <Td textAlign='right'>{wSeq === undefined ? '—' : wSeq}</Td>
                                <Td
                                    textAlign='center' 
                                    paddingRight={anySessionErrors ? 0 : undefined}>
                                    <Status isActive={isActive} isConnected={isConnected} isSelected={isSelected} />
                                </Td>
                                {anySessionErrors && <Td textAlign='center' padding={0}>
                                    {sessionErrors.length > 0
                                        ? 
                                        <IconButton
                                            color='service-red'
                                            icon={<FiAlertTriangle strokeWidth={3}/>}
                                            variant='ghost'
                                            onClick={() => showError(sessionID)}
                                            height='inherit'
                                            minWidth='inherit'
                                            _hover={{ bg: 'none' }}
                                            _focus={{ bg: 'none' }}
                                            _active={{ bg: 'none' }}
                                        />
                                        : <></>}
                                </Td>}
                                <Td>
                                    {!!session.params.consumer &&
                                        <Tooltip openDelay={500} label='View all the FIX messages for this session'>
                                            <IconButton
                                                tabIndex='-1'
                                                onClick={() => onSearchClick(sessionID)}
                                                icon={<FiSearch />}
                                                minWidth='inherit'
                                                height='inherit'
                                                bg='none'
                                                _hover={{ bg: 'none' }}
                                                _focus={{ bg: 'none' }}
                                                _active={{ bg: 'none' }}
                                                />
                                        </Tooltip>
                                    }
                                </Td>
                            </SenderTargetRow>
                        )
                    })}
                </Tbody>
            </Table>
            </TableContainer>
                <Modal 
                    closeOnOverlayClick={false}
                    size={'6xl'}
                    isOpen={isOpen}
                    onClose={onClose}
                    trapFocus={false}
                >
                    <ModalOverlay />
                    <AddSessionModalContent>
                        <AddSessionForm
                            sessions={sessions}
                            editSessionState={selectedSession}
                            isAdvanced={isAdvanced}
                            onAdvancedChange={setIsAdvanced}
                            onClose={onClose}
                            config={config}
                            onFormSubmit={(request) =>
                                sessionManageAction(selectedSession ? 'replaceSession' : 'addSession', '', request)
                            }
                            overrideDefaultValues={sessionDefaults}
                            notify={notify}
                        />
                    </AddSessionModalContent>
                </Modal>
            <Modal size={'md'} isOpen={isSeqNumOpen} onClose={onSeqNumClose}>
                <ModalOverlay />
                <AddSessionModalContent>
                    <ModalCloseButton />
                    <SeqNumModal onApply={onSeqNumSend} />
                </AddSessionModalContent>
            </Modal>
        </SenderTargetFormWrapper>
    </>)
}
